import { GRATUITY_GUIDS } from "../constants";

/**
 * @typedef Check
 * @property {Record<string, any>[]} [appliedDiscounts]
 * @property {Record<string, any>[]} [appliedServiceCharges]
 * @property {Record<string, any>[]} [payments]
 * @property {Record<string, any>[]} [selections]
 */
/**
 * Derives the totals and consolidated line items a receipt renders from.
 *
 * Toast omits empty collections rather than sending empty arrays, and
 * purchases made outside Toast have no order at all, so every collection is
 * defaulted here.
 *
 * @param {Check} [check]
 */
export function getReceiptData(check) {
  const appliedDiscounts = (check?.appliedDiscounts ?? []).map((discount) => {
    return {
      name: discount.name,
      percentage: discount.discountPercent || 0,
      amount: discount.nonTaxDiscountAmount || 0,
    };
  });

  const totalAppliedDiscounts = appliedDiscounts.reduce((total, discount) => {
    return total + discount.amount;
  }, 0);

  const serviceCharges = check?.appliedServiceCharges ?? [];

  const appliedServiceCharges = serviceCharges.map((charge) => {
    if (GRATUITY_GUIDS.includes(charge.serviceCharge?.guid)) {
      return {
        name: "Large Party Gratuity",
        amount: parseFloat(charge.chargeAmount) || 0,
      };
    } else {
      return {
        name: charge.name,
        amount: parseFloat(charge.chargeAmount) || 0,
      };
    }
  });

  // Calculate the total service charges EXCLUDING gratuities
  const totalServiceCharges = serviceCharges.reduce((total, charge) => {
    if (!charge.gratuity) {
      return total + (parseFloat(charge.chargeAmount) || 0);
    }

    return total;
  }, 0);

  const subtotal = (check?.amount ?? 0) - totalServiceCharges || 0;
  const tipAmount = (check?.payments ?? []).reduce(
    (sum, payment) => sum + (payment.tipAmount || 0),
    0,
  );
  const preDiscountSubtotal = subtotal + totalAppliedDiscounts;
  const total = check?.totalAmount || subtotal + tipAmount;

  const itemMap = new Map();

  (check?.selections ?? [])
    .filter((item) => !item.voided)
    .forEach((item) => {
      // Create a unique key for each item based on displayName and price
      const key = `${item.displayName}-${item.price}`;
      const itemQuantity = item.quantity || 1;

      if (itemMap.has(key)) {
        // Item exists, increase quantity
        const existingItem = itemMap.get(key);
        existingItem.quantity += itemQuantity;
      } else {
        // New item, add to map with at least quantity 1
        itemMap.set(key, {
          ...item,
          quantity: itemQuantity,
        });
      }
    });

  const consolidatedItems = Array.from(itemMap.values());

  return {
    total,
    subtotal,
    preDiscountSubtotal,
    tipAmount,
    appliedDiscounts,
    appliedServiceCharges,
    consolidatedItems,
  };
}
